import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import useSEO from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => (slug ? getArticleBySlug(slug) : null), [slug]);
  const relatedArticles = useMemo(
    () => (article ? getRelatedArticles(article.relatedArticles) : []),
    [article],
  );

  useSEO({
    title: article ? article.title : '文章详情',
    description: article ? article.summary : '',
    keywords: article ? article.tags.join(',') : '',
    canonicalUrl: `https://valuablespot.github.io/article/${slug}`,
    ogType: 'article',
    schema: articleSchema,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '掌中之物',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://valuablespot.github.io' },
        { '@type': 'ListItem', position: 2, name: categoryName, item: `https://valuablespot.github.io/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 text-sm text-foreground-400 mb-3">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 flex-wrap mb-6">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[2/1] w-full rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none mb-12
              prose-headings:font-heading prose-headings:text-foreground-900
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-strong:text-foreground-900
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-ul:my-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-10 mt-10">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="flex gap-4 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden group cursor-pointer transition-shadow hover:shadow-lg"
                  >
                    <div className="w-28 h-28 md:w-32 md:h-32 shrink-0 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="py-3 pr-4 flex flex-col justify-center">
                      <h4 className="text-sm font-heading font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{ra.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}